import React, { useCallback, useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { Button, Card, Dot, Input, SectionHeader, Skeleton } from '../../../components/ui';
import type { AiBudget } from '../../../types/aiOps';
import { DEFAULT_BUDGET } from '../../../types/aiOps';
import {
  budgetChanged,
  formatMoney,
  fromBudgetForm,
  toBudgetForm,
  validateBudget,
  type BudgetForm,
} from '../format';
import { Notice } from './ui';

interface BudgetSectionProps {
  budget: AiBudget | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  onSave: (next: AiBudget) => Promise<unknown>;
  className?: string;
}

/**
 * The monthly ceiling and the two lines drawn under it. The thresholds are
 * spelled out in money as well as percent — "scheduled work stops at 80%" is
 * the sentence that stops a surprise.
 */
export const BudgetSection: React.FC<BudgetSectionProps> = ({
  budget,
  loading,
  saving,
  error,
  onSave,
  className = '',
}) => {
  const [form, setForm] = useState<BudgetForm>(toBudgetForm(budget ?? DEFAULT_BUDGET));
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (budget) setForm(toBudgetForm(budget));
  }, [budget]);

  const errors = validateBudget(form);
  const invalid = Object.keys(errors).length > 0;
  const dirty = !!budget && budgetChanged(form, budget);

  const capCents = invalid ? 0 : fromBudgetForm(form).monthlyCapCents;
  const capped = capCents > 0;

  const shareLine = (value: string, when: string): string => {
    const share = Number(value);
    if (!capped || Number.isNaN(share)) return when;
    return `${when} — ${formatMoney((capCents * share) / 100)} of ${formatMoney(capCents)}.`;
  };

  const submit = useCallback(async () => {
    if (invalid) return;
    await onSave(fromBudgetForm(form));
    setJustSaved(true);
    window.setTimeout(() => setJustSaved(false), 2400);
  }, [form, invalid, onSave]);

  const rows: Array<{ key: keyof BudgetForm; label: string; hint: string; suffix: string; max?: number }> = [
    {
      key: 'cap',
      label: 'Monthly ceiling',
      hint: capped
        ? `The most the AI may spend in a month. Currently ${formatMoney(capCents)}.`
        : 'The most the AI may spend in a month. 0 means no limit — nothing will be stopped.',
      suffix: '$',
    },
    {
      key: 'warn',
      label: 'Warn at',
      hint: shareLine(form.warn, 'Admins are told once the month reaches this share'),
      suffix: '%',
      max: 100,
    },
    {
      key: 'stop',
      label: 'Stop scheduled work at',
      hint: shareLine(form.stop, 'Scheduled scans and digests stop at this share; people can still run things by hand'),
      suffix: '%',
      max: 100,
    },
  ];

  return (
    <section className={className}>
      <SectionHeader
        title="Budget"
        aside={
          dirty ? (
            <span className="inline-flex items-center gap-1.5">
              <Dot tone="red" /> Unsaved
            </span>
          ) : justSaved ? (
            <span className="inline-flex animate-fade-up items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} /> Saved
            </span>
          ) : undefined
        }
      />

      <Card divided>
        {loading && !budget
          ? Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="flex items-center justify-between gap-4 px-4 py-3">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-8 w-24" />
              </div>
            ))
          : rows.map((row) => (
              <div key={row.key} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <label htmlFor={`budget-${row.key}`} className="min-w-0 flex-1 cursor-pointer">
                  <span className="block text-[13px] text-ink">{row.label}</span>
                  <span className="mt-0.5 block text-[12px] text-neutral-400">{row.hint}</span>
                </label>
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  {row.suffix === '$' && <span className="text-[13px] text-neutral-400">$</span>}
                  {/* The kit's Input is w-full; a wrapper is the only way to fix its width */}
                  <span className="block w-24">
                    <Input
                      id={`budget-${row.key}`}
                      type="number"
                      inputMode="decimal"
                      className="text-right tabular-nums"
                      min={0}
                      max={row.max}
                      step={row.suffix === '$' ? 1 : 5}
                      value={form[row.key]}
                      aria-invalid={!!errors[row.key]}
                      aria-describedby={errors[row.key] ? `budget-${row.key}-error` : undefined}
                      onChange={(event) => setForm((current) => ({ ...current, [row.key]: event.target.value }))}
                    />
                  </span>
                  {row.suffix === '%' && <span className="w-3 text-[13px] text-neutral-400">%</span>}
                </div>
                {errors[row.key] && (
                  <p id={`budget-${row.key}-error`} className="w-full text-[12px] text-primary-700">
                    {errors[row.key]}
                  </p>
                )}
              </div>
            ))}
      </Card>

      {!loading && !capped && !errors.cap && (
        <Notice className="mt-3" tone="quiet">
          No limit — spending is never blocked. Set a ceiling above 0 to make the two thresholds do anything.
        </Notice>
      )}

      {error && (
        <Notice className="mt-3" tone="error">
          {error}
        </Notice>
      )}

      <div className="mt-3 flex justify-end">
        <Button variant="primary" loading={saving} disabled={!dirty || invalid || loading} onClick={submit}>
          Save
        </Button>
      </div>
    </section>
  );
};
